// Property allowlists per event type. Compared to widget 2.x:
// - Removed DOM-internal flags that carry no behavioural info (bubbles, cancelable,
//   cancelBubble, composed, eventPhase, returnValue).
// - Mouse: removed clientX/Y (identical to x/y), which (alias of button), layerX/Y (deprecated, non-standard).
// - Field names are kept identical to 2.x so existing backend signals consume them unchanged.
const EVENT_PROPERTIES: { [key: string]: string[] } = (() => {
  const map: { [key: string]: string[] } = {};

  const mouseProps = [
    'isTrusted', 'timeStamp', 'type', 'altKey', 'ctrlKey', 'metaKey', 'shiftKey', 'button', 'buttons', 'detail', 'defaultPrevented', 'movementX', 'movementY', 'offsetX', 'offsetY', 'pageX', 'pageY', 'screenX', 'screenY', 'x', 'y',];
  ['click', 'mousedown', 'mouseup', 'mousemove'].forEach(t => { map[t] = mouseProps; });

  const touchProps = ['isTrusted', 'timeStamp', 'type', 'altKey', 'ctrlKey', 'metaKey', 'shiftKey', 'changedTouches', 'targetTouches', 'touches', 'detail', 'defaultPrevented',];
  ['touchstart', 'touchend', 'touchmove', 'touchcancel'].forEach(t => { map[t] = touchProps; });

  const keyboardProps = ['isTrusted', 'timeStamp', 'type', 'altKey', 'ctrlKey', 'metaKey', 'shiftKey', 'isComposing', 'location', 'repeat', 'detail', 'defaultPrevented',];
  ['keydown', 'keyup'].forEach(t => { map[t] = keyboardProps; });

  map['resize'] = ['isTrusted', 'timeStamp', 'type'];

  const focusProps = ['isTrusted', 'timeStamp', 'type', 'defaultPrevented'];
  ['focus', 'blur'].forEach(t => { map[t] = focusProps; });

  const clipboardProps = ['isTrusted', 'timeStamp', 'type', 'defaultPrevented', 'clipboardData'];
  ['copy', 'paste'].forEach(t => { map[t] = clipboardProps; });

  map['scroll'] = ['isTrusted', 'timeStamp', 'type', 'defaultPrevented'];

  map['visibilitychange'] = ['isTrusted', 'timeStamp', 'type'];

  map['wheel'] = [
    'isTrusted', 'timeStamp', 'type',
    'altKey', 'ctrlKey', 'metaKey', 'shiftKey',
    'deltaX', 'deltaY', 'deltaZ', 'deltaMode',
    'button', 'buttons', 'detail', 'defaultPrevented',
    'movementX', 'movementY', 'offsetX', 'offsetY',
    'pageX', 'pageY', 'screenX', 'screenY', 'x', 'y',
  ];

  const pointerProps = [
    'isTrusted', 'timeStamp', 'type',
    'altKey', 'ctrlKey', 'metaKey', 'shiftKey',
    'pointerType', 'pointerId', 'isPrimary',
    'pressure', 'tangentialPressure', 'tiltX', 'tiltY', 'twist',
    'width', 'height',
    'button', 'buttons', 'detail', 'defaultPrevented',
    'movementX', 'movementY', 'offsetX', 'offsetY',
    'pageX', 'pageY', 'screenX', 'screenY', 'x', 'y',
  ];
  ['pointerdown', 'pointermove', 'pointerup', 'pointercancel'].forEach(t => { map[t] = pointerProps; });

  return map;
})();

const FOCUS_DELEGATE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'details',
  'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([disabled])',
  'iframe',
  'object',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[contenteditable]',
].join(', ');

const MOUSEMOVE_THROTTLE_MS = 16;

const MAX_EVENTS = 2500;

interface ListenerRegistration {
  target: EventTarget;
  type: string;
  handler: EventListener;
  options?: AddEventListenerOptions | boolean;
}

export class EventTracker {

  private userEvents: any[] = [];
  private bufferStart = 0;
  private readonly enabled: boolean;
  public active: boolean;

  private listeners: ListenerRegistration[] = [];
  private mousemoveLastFired = 0;
  private pointermoveLastFired = 0;

  public constructor(enabled: boolean = true) {
    this.enabled = enabled;
    this.active = enabled;
    if (enabled) {
      this.registerListeners();
    }
  }

  private registerListeners(): void {
    const save = (event: Event) => this.saveEvent(event);
    this.addListener(document, 'click', save);
    this.addListener(document, 'mousedown', save);
    this.addListener(document, 'mouseup', save);
    this.addListener(document, 'mousemove', this.throttledMousemove);
    this.addListener(document, 'touchstart', save, { passive: true });
    this.addListener(document, 'touchend', save, { passive: true });
    this.addListener(document, 'touchmove', save, { passive: true });
    this.addListener(document, 'touchcancel', save, { passive: true });
    this.addListener(document, 'keydown', save);
    this.addListener(document, 'keyup', save);
    this.addListener(window, 'resize', save);
    this.addListener(window, 'scroll', save, { passive: true });
    this.addListener(document, 'copy', save);
    this.addListener(document, 'paste', save);
    this.addListener(document, 'visibilitychange', this.saveVisibilityChange);
    this.addListener(document, 'wheel', save, { passive: true });

    if (typeof window !== 'undefined' && 'PointerEvent' in window) {
      const savePenOnly = (event: Event) => {
        if ((event as PointerEvent).pointerType === 'pen') {
          this.saveEvent(event);
        }
      };
      const savePenMoveThrottled = (event: Event) => {
        if ((event as PointerEvent).pointerType !== 'pen') return;
        const now = performance.now();
        if (now - this.pointermoveLastFired >= MOUSEMOVE_THROTTLE_MS) {
          this.pointermoveLastFired = now;
          this.saveEvent(event);
        }
      };
      this.addListener(document, 'pointerdown', savePenOnly);
      this.addListener(document, 'pointermove', savePenMoveThrottled);
      this.addListener(document, 'pointerup', savePenOnly);
      this.addListener(document, 'pointercancel', savePenOnly);
    }

    this.addListener(document, 'focusin', this.saveFocusIn, { capture: true });
    this.addListener(document, 'focusout', this.saveFocusOut, { capture: true });
  }

  private addListener(
    target: EventTarget,
    type: string,
    handler: EventListener,
    options?: AddEventListenerOptions | boolean,
  ): void {
    target.addEventListener(type, handler, options);
    this.listeners.push({ target, type, handler, options });
  }

  private throttledMousemove = (event: Event): void => {
    const now = performance.now();
    if (now - this.mousemoveLastFired >= MOUSEMOVE_THROTTLE_MS) {
      this.mousemoveLastFired = now;
      this.saveEvent(event);
    }
  };

  private saveVisibilityChange = (event: Event): void => {
    if (!this.active) return;
    const props = EVENT_PROPERTIES['visibilitychange'];
    const sanitized = this.pickProperties(event, props);
    try {
      sanitized.visibilityState = document.visibilityState;
    } catch {
      // ignore
    }
    this.pushEvent(sanitized);
  };

  private saveFocusIn = (event: Event): void => {
    this.saveDelegatedFocusEvent(event, 'focus');
  };

  private saveFocusOut = (event: Event): void => {
    this.saveDelegatedFocusEvent(event, 'blur');
  };

  private saveDelegatedFocusEvent(event: Event, normalizedType: 'focus' | 'blur'): void {
    if (!this.active) return;
    const target = event.target as Element | null;
    if (!target || typeof target.matches !== 'function') return;
    try {
      if (!target.matches(FOCUS_DELEGATE_SELECTOR)) return;
    } catch {
      return;
    }
    const props = EVENT_PROPERTIES[normalizedType];
    const sanitized = this.pickProperties(event, props);
    sanitized.type = normalizedType;
    this.pushEvent(sanitized);
  }

  private saveEvent(event: Event): void {
    if (!this.active) return;
    const props = EVENT_PROPERTIES[event.type];
    if (!props) return;
    const sanitized = this.pickProperties(event, props);
    this.pushEvent(sanitized);
  }

  private pickProperties(event: Event, props: string[]): any {
    const safeEvent: any = {};
    for (const key of props) {
      try {
        let value = (event as any)[key];
        if (key === 'clipboardData' && value) {
          value = {
            files: value.files ? value.files.length : 0,
            items: value.items ? value.items.length : 0,
            types: value.types ? value.types.length : 0,
          };
        } else if (key === 'changedTouches' || key === 'targetTouches' || key === 'touches') {
          value = this.mapTouchList(value);
        }
        if (typeof value !== 'function') {
          safeEvent[key] = value;
        }
      } catch {
        // Inaccessible properties are silently skipped.
      }
    }
    return safeEvent;
  }

  private mapTouchList(touches: TouchList): any[] {
    const touchesArray: any[] = [];
    if (!touches) return touchesArray;
    for (let i = 0; i < touches.length; i++) {
      const touch = touches[i];
      touchesArray.push({
        clientX: touch.clientX,
        clientY: touch.clientY,
        force: touch.force,
        identifier: touch.identifier,
        pageX: touch.pageX,
        pageY: touch.pageY,
        radiusX: touch.radiusX,
        radiusY: touch.radiusY,
        rotationAngle: touch.rotationAngle,
        screenX: touch.screenX,
        screenY: touch.screenY,
      });
    }
    return touchesArray;
  }

  private pushEvent(event: any): void {
    if (this.userEvents.length >= MAX_EVENTS) {
      this.userEvents[this.bufferStart] = event;
      this.bufferStart = (this.bufferStart + 1) % MAX_EVENTS;
    } else {
      this.userEvents.push(event);
    }
  }

  public getEventsAndResetArray(): any[] {
    let result: any[];
    if (this.userEvents.length < MAX_EVENTS || this.bufferStart === 0) {
      result = this.userEvents;
    } else {
      // Buffer wrapped: reorder into chronological sequence before handing out.
      result = this.userEvents.slice(this.bufferStart).concat(this.userEvents.slice(0, this.bufferStart));
    }
    this.userEvents = [];
    this.bufferStart = 0;
    return result;
  }

  public stop(): void {
    this.active = false;
  }

  public reset(): void {
    this.userEvents = [];
    this.bufferStart = 0;
    this.active = this.enabled;
  }

  public destroy(): void {
    for (const reg of this.listeners) {
      try {
        reg.target.removeEventListener(reg.type, reg.handler, reg.options as any);
      } catch {
        // ignore
      }
    }
    this.listeners = [];
    this.active = false;
    this.userEvents = [];
    this.bufferStart = 0;
  }
}
